// Handles reCAPTCHA v3 execution for any form marked with class="g-recaptcha".
// The form carries a data-recaptcha-sitekey attribute and a hidden g-recaptcha-response
// input; on submit we intercept, fetch a fresh token from Google, populate the hidden
// field, then resubmit. Server-side validation reads the token from that same field name
// (see IRecaptchaService.ValidateAsync), so no other wiring is required per-form.
export class RecaptchaService {
    private submittingForm: HTMLFormElement | null = null;

    public initialize(): void {
        document.addEventListener('submit', (event) => this.handleSubmit(event), true);
    }

    private handleSubmit(event: Event): void {
        const target = event.target as HTMLElement | null;
        const form = target?.closest('form.g-recaptcha') as HTMLFormElement | null;

        if (!form || form === this.submittingForm) {
            return;
        }

        const siteKey = form.dataset.recaptchaSitekey;
        const tokenField = form.querySelector<HTMLInputElement>('[name="g-recaptcha-response"]');

        if (!siteKey || !tokenField || !window.grecaptcha) {
            return;
        }

        // Take full ownership of this submit attempt — stopImmediatePropagation (not just
        // preventDefault) stops the client validation library's own submit listener from
        // also independently submitting the form, which otherwise causes a duplicate POST.
        event.preventDefault();
        event.stopImmediatePropagation();

        if (window.App?.nativeValidation && !window.App.nativeValidation.validate(form)) {
            return;
        }

        window.grecaptcha.ready(() => {
            window.grecaptcha!.execute(siteKey, { action: 'submit' }).then((token) => {
                tokenField.value = token;
                this.submittingForm = form;
                form.submit();
            });
        });
    }
}

declare global {
    interface Window {
        grecaptcha?: {
            ready: (callback: () => void) => void;
            execute: (siteKey: string, options: { action: string }) => Promise<string>;
        };
    }
}
